import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMembership } from "../context/MembershipContext";
import { useI18n } from "../i18n";

/**
 * Reminds the shop's owner to settle their subscription before the shop is
 * locked. The server decides which of the three warnings applies today (see
 * SubscriptionService.stageFor) — this only shows it, and remembers a
 * dismissal.
 *
 * Each warning is dismissed on its own: closing the one-week notice must not
 * hide the three-day one, or the reminders would stop exactly when they start
 * to matter. The key includes the due date, so a renewed subscription (a new
 * date) is warned about afresh. Remembered per device, like the order-alert
 * sound — a cleared browser just shows it again, which errs on the right side.
 *
 * The overdue notice cannot be dismissed: it covers at most the last day
 * before the lock, and hiding it is how someone gets surprised by it.
 */

const STYLES = {
  week: "bg-amber-50 border-amber-300 text-amber-900",
  three_days: "bg-orange-50 border-orange-400 text-orange-900",
  last_day: "bg-red-50 border-red-400 text-red-900",
  overdue: "bg-red-100 border-red-500 text-red-900",
};

const ICONS = { week: "🗓️", three_days: "⏳", last_day: "⚠️", overdue: "🔒" };

const storageKey = (shopId, notice) =>
  `subscription-notice:${shopId}:${notice.due_date || "overdue"}:${notice.stage}`;

const wasDismissed = (key) => {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const SubscriptionBanner = ({ shopId, notice }) => {
  const { t, locale } = useI18n();
  const [, forceRender] = useState(0);
  const payScreen = Boolean(useMembership()?.flag?.("owner_subscription"));

  if (!notice || !shopId) return null;

  const dismissible = notice.stage !== "overdue";
  const key = storageKey(shopId, notice);

  if (dismissible && wasDismissed(key)) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(key, "1");
    } catch {
      // Storage blocked: it just comes back on the next load
    }
    forceRender((n) => n + 1);
  };

  let message;

  if (notice.stage === "overdue") {
    message = notice.locks_at
      ? t("subscription.overdue.locksAt", {
          time: new Date(notice.locks_at).toLocaleString(locale, {
            weekday: "short",
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          }),
        })
      : t("subscription.overdue.soon");
  } else if (notice.stage === "last_day") {
    message = t(`subscription.lastDay.${notice.kind}`);
  } else {
    const area = notice.stage === "week" ? "week" : "threeDays";
    message = t(`subscription.${area}.${notice.kind}`, {
      count: notice.days_left,
      date: new Date(`${notice.due_date}T00:00:00`).toLocaleDateString(locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
    });
  }

  return (
    <div
      role="alert"
      className={`mb-4 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm font-medium ${STYLES[notice.stage]}`}
    >
      <span aria-hidden="true" className="text-lg leading-5">
        {ICONS[notice.stage]}
      </span>
      <p className="flex-1">
        {message}
        {payScreen && (
          <>
            {" "}
            <Link to="/admin/subscription" className="font-bold underline whitespace-nowrap">
              {t("subscription.howToPay")}
            </Link>
          </>
        )}
      </p>
      {dismissible && (
        <button
          type="button"
          onClick={dismiss}
          aria-label={t("subscription.dismiss")}
          className="shrink-0 px-1 opacity-70 hover:opacity-100"
        >
          ✕
        </button>
      )}
    </div>
  );
};

export default SubscriptionBanner;
